import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Ticket } from '../types';
import { Plus, Clock, User, AlertCircle, CheckCircle2 } from 'lucide-react';
const mockTickets: Ticket[] = [{
  id: '1001',
  title: 'Server nicht erreichbar',
  description: 'Der Fileserver antwortet nicht auf Pings.',
  client: 'Müller GmbH',
  priority: 'critical',
  status: 'open',
  assignedTo: 'Max Mustermann',
  createdAt: '2023-10-25 09:00'
}, {
  id: '1002',
  title: 'Drucker Papierstau',
  description: 'HP LaserJet im Flur meldet Fehler.',
  client: 'Praxis Dr. Stein',
  priority: 'low',
  status: 'in_progress',
  assignedTo: 'Lisa Schmidt',
  createdAt: '2023-10-25 10:30'
}, {
  id: '1003',
  title: 'VPN Verbindung bricht ab',
  description: 'Home Office Mitarbeiter haben Verbindungsprobleme.',
  client: 'Schmidt KG',
  priority: 'high',
  status: 'open',
  createdAt: '2023-10-25 11:15'
}, {
  id: '1004',
  title: 'Neuen Benutzer anlegen',
  description: 'Frau Weber fängt am 01.11. an.',
  client: 'Müller GmbH',
  priority: 'medium',
  status: 'waiting',
  assignedTo: 'Max Mustermann',
  createdAt: '2023-10-24 14:00'
}, {
  id: '1005',
  title: 'Outlook startet nicht',
  description: 'Fehlermeldung beim Starten von Outlook.',
  client: 'Schmidt KG',
  priority: 'medium',
  status: 'resolved',
  assignedTo: 'Lisa Schmidt',
  createdAt: '2023-10-24 09:30'
}];
interface TicketManagementProps {
  onLogout: () => void;
}
export function TicketManagement({
  onLogout
}: TicketManagementProps) {
  const [filter, setFilter] = useState<'all' | 'open' | 'my'>('all');
  const getPriorityBadge = (priority: Ticket['priority']) => {
    switch (priority) {
      case 'critical':
        return <Badge variant="error" data-id="element-714">Kritisch</Badge>;
      case 'high':
        return <Badge variant="warning" data-id="element-715">Hoch</Badge>;
      case 'medium':
        return <Badge variant="info" data-id="element-716">Mittel</Badge>;
      case 'low':
        return <Badge variant="secondary" data-id="element-717">Niedrig</Badge>;
    }
  };
  const getStatusIcon = (status: Ticket['status']) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-5 w-5 text-red-500" data-id="element-718" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-blue-500" data-id="element-719" />;
      case 'resolved':
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" data-id="element-720" />;
      default:
        return <Clock className="h-5 w-5 text-slate-400" data-id="element-721" />;
    }
  };
  return <MainLayout title="IT-Service Management" onLogout={onLogout} data-id="element-722">
      <div className="flex justify-between items-center mb-6" data-id="element-723">
        <div className="flex gap-2" data-id="element-724">
          <Button variant={filter === 'all' ? 'primary' : 'secondary'} onClick={() => setFilter('all')} size="sm" data-id="element-725">
            Alle Tickets
          </Button>
          <Button variant={filter === 'open' ? 'primary' : 'secondary'} onClick={() => setFilter('open')} size="sm" data-id="element-726">
            Offen
          </Button>
          <Button variant={filter === 'my' ? 'primary' : 'secondary'} onClick={() => setFilter('my')} size="sm" data-id="element-727">
            Meine Tickets
          </Button>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" data-id="element-729" />} data-id="element-728">Neues Ticket</Button>
      </div>

      <div className="grid grid-cols-1 gap-4" data-id="element-730">
        {mockTickets.map(ticket => <Card key={ticket.id} className="hover:shadow-md transition-shadow cursor-pointer" data-id="element-731">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center" data-id="element-732">
              <div className="flex items-start gap-4" data-id="element-733">
                <div className="mt-1" data-id="element-734">{getStatusIcon(ticket.status)}</div>
                <div data-id="element-735">
                  <div className="flex items-center gap-2 mb-1" data-id="element-736">
                    <span className="text-sm font-mono text-slate-500" data-id="element-737">
                      #{ticket.id}
                    </span>
                    <h3 className="font-semibold text-slate-900" data-id="element-738">
                      {ticket.title}
                    </h3>
                    {getPriorityBadge(ticket.priority)}
                  </div>
                  <p className="text-sm text-slate-600 mb-2" data-id="element-739">
                    {ticket.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-740">
                    <span className="flex items-center gap-1" data-id="element-741">
                      <User className="h-3 w-3" data-id="element-742" />
                      {ticket.client}
                    </span>
                    <span data-id="element-743">•</span>
                    <span data-id="element-744">Erstellt: {ticket.createdAt}</span>
                    {ticket.assignedTo && <>
                        <span data-id="element-745">•</span>
                        <span className="text-teal-600 font-medium" data-id="element-746">
                          Zugewiesen an: {ticket.assignedTo}
                        </span>
                      </>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end md:self-center" data-id="element-747">
                <Button variant="ghost" size="sm" data-id="element-748">
                  Bearbeiten
                </Button>
                <Button variant="outline" size="sm" data-id="element-749">
                  Details
                </Button>
              </div>
            </div>
          </Card>)}
      </div>
    </MainLayout>;
}